import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { X, User, Lock, Mail, Phone, UserPlus, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import LogoPlaceholder from '../ui/LogoPlaceholder';
import { authAPI } from '../../services/api';

export function SignUpForm({ onClose, onSignUp, onSwitchToLogin }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    organization: '',
    municipality: '',
    username: '',
    password: '',
    confirmPassword: '',
    role: 'user'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [pendingSuccessMessage, setPendingSuccessMessage] = useState('');

  const municipalities = [
    'Tagbilaran City', 'Baclayon', 'Alburquerque', 'Loboc', 'Sikatuna',
    'Carmen', 'Garcia Hernandez', 'Valencia', 'Dimiao', 'Dauis',
    'Panglao', 'Cortes', 'Maribojoc', 'Antequera', 'Loon',
    'Calape', 'Tubigon', 'Clarin', 'Inabanga', 'Sagbayan',
    'Catigbian', 'Batuan', 'Balilihan', 'Sevilla',
    'Bilar', 'Candijay', 'Guindulman', 'Anda', 'Duero',
    'Jagna', 'Sierra Bullones', 'San Miguel', 'Ubay', 'Trinidad',
    'Talibon', 'Bien Unido', 'Buenavista', 'Getafe', 'President Carlos P. Garcia',
    'Mabini', 'Pilar', 'Dagohoy'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field-specific errors when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Phone: digits only, max 11, live validation
  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
    setFormData(prev => ({ ...prev, phone: digits }));
    if (digits.length === 0) {
      setErrors(prev => ({ ...prev, phone: '' }));
    } else if (digits.length < 11) {
      setErrors(prev => ({ ...prev, phone: `Phone number must be 11 digits (${digits.length}/11 entered)` }));
    } else {
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.municipality) newErrors.municipality = 'Municipality is required';
    if (!formData.username.trim()) newErrors.username = 'Username is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (!formData.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone: must be exactly 11 digits
    if (formData.phone && formData.phone.length !== 11) {
      newErrors.phone = `Phone number must be exactly 11 digits (${formData.phone.length}/11 entered)`;
    }
    if (formData.phone && /\D/.test(formData.phone)) {
      newErrors.phone = 'Phone number must contain numbers only';
    }

    // Username validation
    if (formData.username && formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters long';
    }

    // Password validation
    if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }

    // Password confirmation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Prepare user data for API
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        organization: formData.organization,
        municipality: formData.municipality,
        username: formData.username,
        password: formData.password,
        role: formData.role || 'user'
      };

      // Call the API to create the user account
      const response = await authAPI.signup(userData);
      if (response.success) {
        if (response.isPendingApproval) {
          setPendingSuccessMessage(response.message || `Registration Successful! Your ${userData.role === 'farmer' ? 'Farmer' : 'Processor'} account application is pending Admin approval.`);
          return;
        }

        const newUser = {
          id: response.user?.id,
          username: response.user?.username || userData.username,
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          phone: userData.phone,
          organization: userData.organization,
          municipality: userData.municipality,
          role: response.user?.role || 'user',
          avatar: null
        };

        onSignUp(newUser);
        onClose();
      } else {
        throw new Error(response.message || 'Registration failed');
      }
      
    } catch (error) {
      // Handle different types of errors
      if (error.message.includes('username') || error.message.includes('already exists')) {
        setErrors({ username: error.message });
      } else if (error.message.includes('email')) {
        setErrors({ email: error.message });
      } else {
        setErrors({ general: error.message || 'Registration failed. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const modalRef = useRef(null);

  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  if (pendingSuccessMessage) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm animate-fade-in">
        <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center shadow-2xl animate-scale-in space-y-4">
          <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto text-amber-600 font-bold text-2xl">
            ⏳
          </div>
          <h2 className="text-lg font-bold text-amber-950">Application Submitted!</h2>
          <p className="text-xs text-gray-600 leading-relaxed">
            {pendingSuccessMessage}
          </p>
          <div className="pt-2">
            <Button
              onClick={() => {
                onClose();
                onSwitchToLogin();
              }}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-xl text-xs py-2.5 shadow"
            >
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="signup-dialog-title"
      ref={modalRef}
    >
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl transform transition-all duration-300 animate-scale-in">
        <Card className="border-0 shadow-none">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <LogoPlaceholder size="medium" showText={false} className="" />
                <CardTitle className="text-amber-900" id="signup-dialog-title">Create Account</CardTitle>
              </div>
              <Button variant="ghost" onClick={onClose} className="text-gray-500 hover:text-gray-700 rounded-full h-8 w-8 p-0 transition-all duration-200 hover:bg-gray-100" aria-label="Close">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="text-green-800 border-b pb-2">Personal Information</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        id="firstName"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        placeholder="Enter your first name"
                        className="pl-10 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-green-500"
                        required
                      />
                    </div>
                    {errors.firstName && <p className="text-red-500 text-sm">{errors.firstName}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        id="lastName"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        placeholder="Enter your last name"
                        className="pl-10 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-green-500"
                        required
                      />
                    </div>
                    {errors.lastName && <p className="text-red-500 text-sm">{errors.lastName}</p>}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="your.email@example.com"
                        className="pl-10 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-green-500"
                        required
                      />
                    </div>
                    {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        id="phone"
                        type="tel"
                        inputMode="numeric"
                        maxLength={11}
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        placeholder="09XXXXXXXXX"
                        className={`pl-10 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-green-500 ${
                          errors.phone ? 'border-red-400 focus:ring-red-400 bg-red-50' : ''
                        }`}
                        required
                      />
                    </div>
                    {errors.phone
                      ? <p className="text-red-500 text-xs font-medium">{errors.phone}</p>
                      : formData.phone.length > 0
                        ? <p className="text-green-600 text-xs font-medium">✓ {formData.phone.length}/11 digits</p>
                        : <p className="text-gray-400 text-xs">Numbers only · exactly 11 digits (e.g. 09171234567)</p>
                    }
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="organization">Organization (Optional)</Label>
                    <Input
                      id="organization"
                      value={formData.organization}
                      onChange={(e) => handleInputChange('organization', e.target.value)}
                      placeholder="Cooperative, NGO, Company, etc."
                      className="rounded-xl transition-all duration-200 focus:ring-2 focus:ring-green-500"
                    />
                  </div>

              {/* Role Selection */}
              <div className="space-y-2">
                <Label htmlFor="role">Account Type *</Label>
                <Select value={formData.role} onValueChange={(value) => handleInputChange('role', value)}>
                  <SelectTrigger id="role">
                    <SelectValue placeholder="Select your account type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">🌐 General User (Researcher / Visitor)</SelectItem>
                    <SelectItem value="farmer">🌱 Cacao Farmer</SelectItem>
                    <SelectItem value="processor">🏭 Cacao Processor / Business</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  {formData.role === 'farmer' && '🌱 As a Farmer, you can request to list your cacao farm profile in the public Farmers Directory.'}
                  {formData.role === 'processor' && '🏭 As a Processor, you can request to list your business profile in the public Processors Directory.'}
                  {formData.role === 'user' && '🌐 General users can browse directories, research, maps and analytics.'}
                </p>
              </div>

                  <div className="space-y-2">
                    <Label htmlFor="municipality">Municipality *</Label>
                    <Select value={formData.municipality} onValueChange={(value) => handleInputChange('municipality', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select your municipality" />
                      </SelectTrigger>
                      <SelectContent>
                        {municipalities.map(municipality => (
                          <SelectItem key={municipality} value={municipality}>
                            {municipality}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.municipality && <p className="text-red-500 text-sm">{errors.municipality}</p>}
                  </div>
                </div>
              </div>


              {/* Account Information */}
              <div className="space-y-4">
                <h3 className="text-green-800 border-b pb-2">Account Information</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="username">Username *</Label>
                  <div className="relative">
                    <UserPlus className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      id="username"
                      value={formData.username}
                      onChange={(e) => handleInputChange('username', e.target.value)}
                      placeholder="Choose a unique username"
                      className="pl-10 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>
                  {errors.username && <p className="text-red-500 text-sm">{errors.username}</p>}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">Password *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        placeholder="At least 6 characters"
                        className="pl-10 pr-10 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-green-500"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {errors.password && <p className="text-red-500 text-sm">{errors.password}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        placeholder="Re-enter your password"
                        className="pl-10 pr-10 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-green-500"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {errors.confirmPassword && <p className="text-red-500 text-sm">{errors.confirmPassword}</p>}
                  </div>
                </div>
              </div>

              {/* General Error Message */}
              {errors.general && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-red-600 text-sm">{errors.general}</p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={onSwitchToLogin}
                  className="flex-1"
                >
                  Already have an account? Login
                </Button>
              </div>

              <div className="pt-2">
                <Button 
                  type="button"
                  variant="ghost" 
                  onClick={onClose}
                  className="w-full text-gray-600 hover:text-gray-800 flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Welcome Page
                </Button>
              </div>
            </form>

            <div className="mt-6 p-4 bg-blue-50 rounded-2xl border border-blue-100">
              <h4 className="text-sm text-blue-800 mb-2 font-semibold">Account Information:</h4>
              <div className="text-xs text-blue-700">
                <p>• New accounts are created with "User" access level</p>
                <p>• You'll be able to view farmers, processors, and analytics data</p>
                <p>• Contact admin for elevated permissions if needed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}